import React, { useState, useEffect } from "react";
import styled from 'styled-components';
import { Modal, Button, Dropdown } from "react-bootstrap";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

import { fetchPrivacyTypeAPI, updatePlaylistAPI } from "../../controller/userController";
import { StyledDropdownItem } from "../common/DropDownMenu/StyledDropDownMenuItem";

const FormGroup = styled.div`
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FormLabel = styled.label`
    font-weight: 600;
    color: #1a1a1a;
    font-size: 14px;
`;

const FormInput = styled.input`
    padding: 12px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #06b6d4;
        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const FormSelect = styled.select`
    padding: 12px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #1a1a1a;

    &:focus {
        outline: none;
        border-color: #06b6d4;
        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
    }
`;

const TagInputContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const TagInput = styled.input`
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #06b6d4;
        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
    }
`;

const AddTagButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0f766e;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(15, 118, 110, 0.1);
        color: #06b6d4;
    }

    &:active {
        transform: scale(0.9);
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const Tag = styled.div`
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RemoveTagButton = styled.button`
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 16px;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.15);
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
`;

const SaveButton = styled(Button)`
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%) !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(15, 118, 110, 0.3) !important;
    }
`;

const CancelButton = styled(Button)`
    background: #f0f0f0 !important;
    color: #1a1a1a !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;

    &:hover {
        background: #e0e0e0 !important;
    }
`;

const EditButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #0f766e;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    transition: all 0.2s ease;

    &:hover {
        color: #06b6d4;
        transform: scale(1.15);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const StyledModal = styled(Modal)`
    .modal-content {
        border-radius: 12px;
        border: none;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
        border-bottom: 1px solid #f0f0f0;
        padding: 20px;

        h5 {
            font-weight: 700;
            color: #1a1a1a;
        }
    }

    .modal-body {
        padding: 24px;
    }

    .modal-footer {
        border-top: 1px solid #f0f0f0;
        padding: 16px 20px;
        background: #fafafa;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
    }

    .btn-close {
        filter: brightness(0.5);

        &:hover {
            filter: brightness(0.7);
        }
    }
`;

export default function EditPlaylist(props) {
    const [show, setShow] = useState(false);
    const [privacy, setPrivacy] = useState([]);
    const [tagText, setTagText] = useState("");
    const [playlistInfo, setPlaylistInfo] = useState({
        title: "",
        tags: [],
        privacy: 1,
    });

    const updatePlaylistCallback = () => {
        props.refresh();
    };

    useEffect(() => {
        fetchPrivacyTypeAPI(setPrivacy);
        if (props.playlist.tags == null) {
            props.playlist.tags = [];
            setPlaylistInfo(props.playlist);
        } else {
            setPlaylistInfo(props.playlist);
        }
        // eslint-disable-next-line
    }, []);

    const handleCancel = () => {
        setShow(false);
    };

    const handleSave = () => {
        updatePlaylistAPI(playlistInfo, updatePlaylistCallback);
        props.refresh();
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    const addTag = () => {
        if (tagText.trim()) {
            setPlaylistInfo({
                ...playlistInfo,
                tags: [...playlistInfo.tags, tagText],
            });
            setTagText("");
        }
    };

    const removeTag = (tagToRemove) => {
        setPlaylistInfo({
            ...playlistInfo,
            tags: playlistInfo.tags.filter((tag) => tag !== tagToRemove),
        });
    };

    return (
        <>
            {props.buttonType == 'dropdown' ? (
                <StyledDropdownItem onClick={handleShow}>
                    <EditIcon sx={{ fontSize: '18px' }} />
                    Edit Playlist
                </StyledDropdownItem>
            ) : null}

            {props.buttonType == 'verticon' ? (
                <EditButton onClick={handleShow} title="Edit playlist">
                    <MoreVertIcon sx={{ fontSize: '20px' }} />
                </EditButton>
            ) : null}

            <StyledModal size="lg" show={show} onHide={handleCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Playlist: {props.playlist.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormGroup>
                        <FormLabel>Playlist Title</FormLabel>
                        <FormInput
                            type="text"
                            placeholder="Enter playlist name"
                            value={playlistInfo.title}
                            onChange={(e) =>
                                setPlaylistInfo({
                                    ...playlistInfo,
                                    title: e.target.value,
                                })
                            }
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>Privacy</FormLabel>
                        <FormSelect
                            value={playlistInfo.privacy}
                            onChange={(e) =>
                                setPlaylistInfo({
                                    ...playlistInfo,
                                    privacy: parseInt(e.target.value),
                                })
                            }
                        >
                            {privacy?.map((pv) => (
                                <option key={pv.privacy_id} value={pv.privacy_id}>
                                    {pv.privacy_name}
                                </option>
                            ))}
                        </FormSelect>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>Tags</FormLabel>
                        <TagInputContainer>
                            <TagInput
                                type="text"
                                placeholder="Enter tag and click add"
                                value={tagText}
                                onChange={(e) => setTagText(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addTag();
                                    }
                                }}
                            />
                            <AddTagButton onClick={addTag} title="Add tag">
                                <AddCircleOutlineIcon sx={{ fontSize: '24px' }} />
                            </AddTagButton>
                        </TagInputContainer>
                        {playlistInfo.tags && playlistInfo.tags.length > 0 && (
                            <TagsContainer>
                                {playlistInfo.tags.map((tag) => (
                                    <Tag key={tag}>
                                        <span>{tag}</span>
                                        <RemoveTagButton
                                            onClick={() => removeTag(tag)}
                                            title="Remove tag"
                                        >
                                            <CloseIcon sx={{ fontSize: '14px' }} />
                                        </RemoveTagButton>
                                    </Tag>
                                ))}
                            </TagsContainer>
                        )}
                    </FormGroup>
                </Modal.Body>

                <Modal.Footer>
                    <CancelButton onClick={handleCancel}>
                        Cancel
                    </CancelButton>
                    <SaveButton onClick={handleSave}>
                        Save
                    </SaveButton>
                </Modal.Footer>
            </StyledModal>
        </>
    );
}
